import { Checkbox } from "components/designV2/checkbox/checkbox";
import { AbsenceType } from "model/Company";
import React from "react";
import { useTranslation } from "react-i18next";
import { SettingsCheckboxGrid } from "./passSettingsV2.styled";
import { SystemSettingRow } from "./systemSettingRow.component";

type Props = {
  value: string;
  onChange: (v: string) => void;
};

const NONE_ABSENCE_VALUE = "None";

const stringToArray = (v: unknown): string[] => {
  if (Array.isArray(v)) return v.map((x) => String(x));
  if (typeof v === "string") {
    const s = v.trim();
    if (!s) return [];
    return s
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);
  }
  return [];
};

const enforceNoneRules = (arr: string[]): string[] => {
  const unique = Array.from(new Set(arr.filter(Boolean).map(String)));

  if (unique.length === 0) return [NONE_ABSENCE_VALUE];

  const hasNonNone = unique.some((x) => x !== NONE_ABSENCE_VALUE);
  if (hasNonNone) return unique.filter((x) => x !== NONE_ABSENCE_VALUE);

  return [NONE_ABSENCE_VALUE];
};

const arrayToStringForBackend = (arr: string[]): string =>
  enforceNoneRules(arr).join(", ");

export const CompanyMonthlySalaryIgnoreAbsenceSetting: React.FC<Props> = ({
  value,
  onChange,
}) => {
  const { t } = useTranslation();

  const selectedArray = enforceNoneRules(stringToArray(value));
  const selectedSet = new Set(
    selectedArray.filter((entry) => entry !== NONE_ABSENCE_VALUE),
  );
  const isSelected = (type: string) => selectedSet.has(type);

  const toggle =
    (type: string) =>
    (isChecked: boolean): void => {
      const nextSet = new Set(selectedSet);
      if (isChecked) nextSet.add(type);
      else nextSet.delete(type);

      const nextArr = Array.from(nextSet);
      const finalArr = enforceNoneRules(nextArr);
      onChange(arrayToStringForBackend(finalArr));
    };

  return (
    <SystemSettingRow
      title={t("System.CompanyMonthlySalaryIgnoreAbsence")}
      description={t("System.CompanyMonthlySalaryIgnoreAbsenceInfo")}
      controlVerticalAlign="start"
    >
      <SettingsCheckboxGrid>
        <Checkbox
          label={t("System.SickLeave")}
          checked={isSelected(AbsenceType.SICK_LEAVE) ? "checked" : "unchecked"}
          onChange={toggle(AbsenceType.SICK_LEAVE)}
        />
        <Checkbox
          label={t("System.SickLeaveChild")}
          checked={
            isSelected(AbsenceType.SICK_LEAVE_CHILD) ? "checked" : "unchecked"
          }
          onChange={toggle(AbsenceType.SICK_LEAVE_CHILD)}
        />
        <Checkbox
          label={t("System.ParentalLeave")}
          checked={
            isSelected(AbsenceType.PARENTAL_LEAVE) ? "checked" : "unchecked"
          }
          onChange={toggle(AbsenceType.PARENTAL_LEAVE)}
        />
        <Checkbox
          label={t("System.Vacation")}
          checked={isSelected(AbsenceType.VACATION) ? "checked" : "unchecked"}
          onChange={toggle(AbsenceType.VACATION)}
        />
        <Checkbox
          label={t("System.Other")}
          checked={isSelected(AbsenceType.OTHER) ? "checked" : "unchecked"}
          onChange={toggle(AbsenceType.OTHER)}
        />
        <Checkbox
          label={t("System.Unreported")}
          checked={isSelected(AbsenceType.UNREPORTED) ? "checked" : "unchecked"}
          onChange={toggle(AbsenceType.UNREPORTED)}
        />
      </SettingsCheckboxGrid>
    </SystemSettingRow>
  );
};
